import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { lastValueFrom, Subscription } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { MessageRejectedException } from 'src/app/shared/errors/cognito/cognito-errors';
import { RepresentativeLimitReachedException } from 'src/app/shared/errors/user-management-service/user-management-service-errors';
import { BusinessLevels } from 'src/app/shared/models/constants/vip-levels';
import { DataUpdateFeedback } from 'src/app/shared/models/data-update-feedback/data-update-feedback';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { InviteRepresentativeRequestDTO } from 'src/app/shared/models/dtos/requests/invite-representative-request-dto/invite-representative-request-dto';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';
import { CouponVip } from 'src/app/shared/models/dtos/responses/coupon-vip/coupon-vip';
import { InviteRepresentativeResponseDTO } from 'src/app/shared/models/dtos/responses/invite-representative-response-dto/invite-representative-response-dto';
import { notInValidator } from 'src/app/shared/validators/registration-validators';

@Component({
  selector: 'app-customer-summary',
  templateUrl: './customer-summary.component.html',
  styleUrls: ['./customer-summary.component.scss'],
})

/**
 * Customer summary
 */
export class CustomerSummaryComponent implements OnInit {
  @Input() businessId!: number;
  @Input() viewOnly!: boolean;
  @Output() businessUpdated = new EventEmitter<void>();
  businessSumary!: BusinessSummaryResponseDTO;
  manageRepresentatives!: UntypedFormGroup;
  representativeList: Representative[] = [];
  private subsink = new Subscription();
  inviteErrorFeedback = new DataUpdateFeedback();
  inviteLoading = false;
  inviteeList: InviteRepresentativeResponseDTO[] = [];
  loading: boolean = true;
  nRepresentatives!: number;
  resellerName!: string | undefined;
  businessVIPCoupons: CouponVip[] = [];
  private hasLoadedRepresentativeCount = false;

  /**
   * @constructor
   */
  constructor(
    private userService: UserService,
    private authenticatedUserService: AuthenticatedUserService
  ) {}

  /**
   * on init
   */
  ngOnInit(): void {
    this.refreshBusinessData();

    this.manageRepresentatives = new UntypedFormGroup({
      email: new UntypedFormControl('', [
        Validators.required,
        Validators.email,
        notInValidator(
          this.representativeList,
          (el: Representative) => el.email || ''
        ),
      ]),
    });

    this.subsink.add(
      this.manageRepresentatives.valueChanges.subscribe(() => {
        this.inviteErrorFeedback.reset();
      })
    );
  }

  /**
   * Refreshs the business data
   */
  refreshBusinessData() {
    this.userService
      .getBusinessSummary(this.businessId)
      .subscribe((summary) => {
        this.businessSumary = summary;
        if (this.hasVIPLevel(summary)) {
          this.userService.getCouponVipByBusinessId(this.businessId).subscribe({
            next: (coupons: CouponVip[]) => {
              this.businessVIPCoupons = coupons;
              this.loading = false;
            },
            error: (err) => {
              this.loading = false;
            },
          });
        } else {
          this.loading = false;
        }
      });
  }

  /**
   * @return {boolean}
   */
  isInviteRepresentativeButtonDisabled(): boolean {
    return this.inviteLoading || this.manageRepresentatives.invalid;
  }

  /**
   * Send an invitation for a possible business representative
   * @param {string} emailToInvite
   */
  async inviteBusinessRepresentative(emailToInvite?: string) {
    let tookFromForm = false;
    let email: string;
    if (emailToInvite && emailToInvite.length > 0) {
      email = emailToInvite;
    } else if (this.manageRepresentatives.invalid) {
      throw new Error('invalid form');
    } else {
      email = this.manageRepresentatives.get('email')!.value;
      tookFromForm = true;
    }
    this.inviteLoading = true;
    const inviteRepresentativeRequestDTO = new InviteRepresentativeRequestDTO(
      email,
      this.businessId
    );
    try {
      await lastValueFrom(
        this.userService.inviteRepresentative(inviteRepresentativeRequestDTO)
      );
      this.updateRepresentativesAndInvitees();
      if (tookFromForm) {
        const emailControl = this.manageRepresentatives.get('email')!;
        emailControl.reset('');
      }
    } catch (error) {
      this.handleInviteError(error as Error);
    } finally {
      this.inviteLoading = false;
    }
  }

  /**
   * Populates invitees list from backend
   */
  async pullInviteeList() {
    const inviteeList = await lastValueFrom(
      this.userService.listInvitees(this.businessId)
    );
    this.inviteeList.splice(0, this.inviteeList.length);
    this.inviteeList.push(...inviteeList);
  }

  /**
   * Updates both lists
   */
  async updateRepresentativesAndInvitees() {
    await this.pullInviteeList();
  }

  /**
   * Handles possible errors in inviting a representative
   * @param {Error} error
   */
  private handleInviteError(error: Error) {
    let errorMsg = 'backend-comm-error';
    if (error instanceof MessageRejectedException) {
      errorMsg = 'invite-exception-message-rejected';
    } else if (error instanceof RepresentativeLimitReachedException) {
      errorMsg = 'invite-exception-rlr-001';
    }
    this.inviteErrorFeedback.setError(errorMsg);
  }

  /**
   * Counts number of representatives
   * and reloads summary on subsequent changes, since business status
   * (active/disabled) depends on it and is computed by the backend
   * @param {number} e
   */
  representativeCount(e: number) {
    this.nRepresentatives = e;
    const isFirstLoad = !this.hasLoadedRepresentativeCount;
    this.hasLoadedRepresentativeCount = true;

    if (isFirstLoad) {
      return;
    }

    this.userService
      .getBusinessSummary(this.businessId)
      .subscribe((summary) => {
        this.businessSumary = summary;
        this.loading = false;
        this.businessUpdated.emit();
      });
  }

  /**
   * Checks if business has a VIP level
   * @param {BusinessSummaryResponseDTO} summary
   * @return {boolean}
   */
  hasVIPLevel(summary: BusinessSummaryResponseDTO): boolean {
    for (const businessLevel of summary.businessLevels!) {
      if (!!businessLevel && businessLevel.level > BusinessLevels.ENTRY_LEVEL) {
        return true;
      }
    }
    return false;
  }
}
